import { useCallback, useEffect, useRef, useState } from 'react';
import { throttle } from '../Utils';

const inputEvents = [
    'keydown',
    'keyup',
    'keypress',
    'mousedown',
    'mouseup',
    'mousemove',
    'click',
    'dblclick',
    'contextmenu',
    'wheel',
    'pointerdown',
    'pointerup',
    'pointermove',
];

const LAST_ACTIVITY_KEY = 'motili-last-activity';

/**
 * Custom hook to detect user inactivity (idle state) based on input events and page visibility.
 * Syncs activity across tabs via localStorage so that activity in any tab prevents idle timeout in all tabs.
 * @param milliseconds - The duration of inactivity (in milliseconds) after which the user is considered idle.
 * @returns A tuple of [idle, reset] where idle is a boolean and reset programmatically restarts the idle timer.
 */
export function useIdle(milliseconds: number): [boolean, () => void] {
    const [idle, setIdle] = useState(false);
    const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
    const handleEventRef = useRef<() => void>();

    useEffect(() => {
        const updateLastActivity = () => {
            localStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString());
        };

        const getLastActivity = (): number =>
            Number(localStorage.getItem(LAST_ACTIVITY_KEY)) || Date.now();

        const handleTimeout = () => {
            const timeSinceLastActivity = Date.now() - getLastActivity();
            if (timeSinceLastActivity < milliseconds) {
                // Another tab was active — reschedule with remaining time
                const remaining = milliseconds - timeSinceLastActivity;
                globalThis.clearTimeout(timeoutRef.current);
                timeoutRef.current = globalThis.setTimeout(
                    handleTimeout,
                    remaining
                );
                return;
            }
            setIdle(true);
        };

        const handleEvent = throttle(() => {
            setIdle(false);
            updateLastActivity();

            globalThis.clearTimeout(timeoutRef.current);
            timeoutRef.current = globalThis.setTimeout(
                handleTimeout,
                milliseconds
            );
        }, 500);

        const handleVisibilityChange = () => {
            if (!document.hidden) {
                handleEvent();
            }
        };

        const handleStorageChange = (e: StorageEvent) => {
            if (e.key === LAST_ACTIVITY_KEY && e.newValue) {
                setIdle(false);
                globalThis.clearTimeout(timeoutRef.current);
                const timeSinceActivity = Date.now() - Number(e.newValue);
                const remaining = Math.max(milliseconds - timeSinceActivity, 0);
                timeoutRef.current = globalThis.setTimeout(
                    handleTimeout,
                    remaining
                );
            }
        };

        handleEventRef.current = handleEvent;
        updateLastActivity();
        timeoutRef.current = globalThis.setTimeout(handleTimeout, milliseconds);
        inputEvents.forEach(event =>
            globalThis.addEventListener(event, handleEvent)
        );

        document.addEventListener('visibilitychange', handleVisibilityChange);
        globalThis.addEventListener('storage', handleStorageChange);
        return () => {
            inputEvents.forEach(event =>
                globalThis.removeEventListener(event, handleEvent)
            );
            document.removeEventListener(
                'visibilitychange',
                handleVisibilityChange
            );
            globalThis.removeEventListener('storage', handleStorageChange);
            globalThis.clearTimeout(timeoutRef.current);
        };
    }, [milliseconds]);

    const reset = useCallback(() => {
        handleEventRef.current?.();
    }, []);

    return [idle, reset];
}
